/**
 * A decimal quantity in millionths (six places, like the `numeric(18,6)` columns that store it). PRD
 * invariant 4: quantities are exact decimals, never binary floating point.
 */
export type Quantity = bigint;

const PLACES = 6;
const ONE = 10n ** BigInt(PLACES);
const DECIMAL = /^(-?)(\d+)(?:\.(\d{1,6}))?$/;

/** Parses a plain decimal ("250", "-0.5", "18.250000"); throws RangeError on anything else. */
export function parseQuantity(text: string): Quantity {
  const match = DECIMAL.exec(text);
  if (!match) throw new RangeError(`Expected a decimal with at most ${PLACES} places, got "${text}"`);
  const [, sign, whole = "", fraction = ""] = match;
  const value = BigInt(whole) * ONE + BigInt(fraction.padEnd(PLACES, "0"));
  return sign ? -value : value;
}

/** The shortest exact decimal text: "250", "0.5", "-18.25". */
export function formatQuantity(quantity: Quantity): string {
  const sign = quantity < 0n ? "-" : "";
  const abs = quantity < 0n ? -quantity : quantity;
  const fraction = (abs % ONE).toString().padStart(PLACES, "0").replace(/0+$/, "");
  return `${sign}${abs / ONE}${fraction ? `.${fraction}` : ""}`;
}

/** `numerator / denominator`, rounded half away from zero (the sign lives in the numerator). */
function divideRounded(numerator: bigint, denominator: bigint): bigint {
  const half = denominator / 2n;
  return numerator < 0n ? (numerator - half) / denominator : (numerator + half) / denominator;
}

/**
 * A unit of measure: its dimension (mass, volume, count) and how many of the dimension's base unit
 * (g, ml, pcs) one of it holds.
 */
export interface Unit {
  dimension: string;
  toBase: Quantity;
}

export type Conversion = { ok: true; quantity: Quantity } | { ok: false; reason: "dimension_mismatch" };

/**
 * Converts between units of one dimension, rounding past six places half away from zero. Crossing
 * dimensions (g → ml) is refused: that needs a density, which no ingredient carries (PRD invariant 4).
 */
export function convertQuantity(quantity: Quantity, from: Unit, to: Unit): Conversion {
  if (from.dimension !== to.dimension) return { ok: false, reason: "dimension_mismatch" };
  return { ok: true, quantity: divideRounded(quantity * from.toBase, to.toBase) };
}

/** `quantity / divisor`, rounded past six places half away from zero. */
export function divideQuantity(quantity: Quantity, divisor: Quantity): Quantity {
  return divideRounded(quantity * ONE, divisor);
}
